import React from "react";

import Chart from "react-apexcharts";

import {
  barChartConfig,
  comparisonSeriesConfig,
  comparisonStrokeConfig,
  comparisonFillConfig,
  CHART_COLORS,
} from "../../../utils/chartConfigs";
import EmptyState from "../shared/EmptyState";

/**
 * Revenue bar chart with optional year-over-year comparison overlay.
 *
 * When `showComparison` is true and `previousData` is provided, renders two
 * bar series (solid vs dashed/30% opacity) with a delta tooltip.
 *
 * @param {{
 *   currentData: number[],
 *   previousData?: number[],
 *   categories: string[],
 *   height?: number,
 *   showComparison?: boolean
 * }} props
 */
const RevenueChart = ({
  currentData,
  previousData,
  categories,
  height,
  showComparison,
}) => {
  if (!currentData || currentData.length === 0) {
    return <EmptyState message="No revenue data available" />;
  }

  const hasPrevious = previousData && previousData.length > 0;
  const isComparing = showComparison && hasPrevious;

  let config;

  if (isComparing) {
    // Build comparison series
    const compSeries = comparisonSeriesConfig(
      currentData,
      previousData,
      "This Period",
      "Previous Period"
    );

    // For bar chart comparison, convert line-type series to plain bar series
    const barSeries = compSeries.map(({ name, data }) => ({ name, data }));

    // Get base bar config
    config = barChartConfig(barSeries, categories);

    // Apply comparison stroke and fill overrides
    const strokeOverrides = comparisonStrokeConfig();
    const fillOverrides = comparisonFillConfig();

    config.stroke = { ...config.stroke, ...strokeOverrides };
    config.fill = { ...config.fill, ...fillOverrides };
    config.colors = [CHART_COLORS.primary, CHART_COLORS.gray];

    // Custom tooltip showing both values + delta
    config.tooltip = {
      ...config.tooltip,
      shared: true,
      intersect: false,
      custom: ({ series: s, dataPointIndex, w }) => {
        const current = s[0]?.[dataPointIndex] || 0;
        const previous = s[1]?.[dataPointIndex] || 0;
        const delta =
          previous !== 0
            ? (((current - previous) / previous) * 100).toFixed(1)
            : "N/A";
        const sign = current >= previous ? "+" : "";
        const cat = w.globals.categoryLabels?.[dataPointIndex] || "";

        const formatVal = (val) => {
          if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;

          return `$${val}`;
        };

        return `
          <div class="apexcharts-tooltip-custom" style="padding:8px 12px;font-size:13px;line-height:1.5">
            <div style="font-weight:600;margin-bottom:4px">${cat}</div>
            <div>${formatVal(current)} vs ${formatVal(previous)}
              <span style="color:${current >= previous ? "#10b981" : "#ef4444"};font-weight:600">
                (${delta !== "N/A" ? `${sign + delta}%` : delta})
              </span>
            </div>
          </div>
        `;
      },
    };
  } else {
    // Single-series pill-shaped bar chart
    config = barChartConfig(
      [{ name: "Revenue", data: currentData }],
      categories
    );
  }

  // Separate series from options
  const { series, ...options } = config;

  return (
    <Chart
      height={height || 350}
      options={options}
      series={series}
      type="bar"
    />
  );
};

export default RevenueChart;
